import React from 'react';
import { DdtBeforeOpenEventArgs, DdtChangeEventArgs, DropDownTreeComponent } from '@syncfusion/ej2-react-dropdowns';

interface KnowledgeLibraryOntologyFilterProps {
    facilityHierarchy: IFacilityHierarchyPart | null;
    selectedOntologyId?: string;
    onChange: (ontologyId?: string) => void;
    readonly?: boolean;
    showClearButton?: boolean;
}

interface KnowledgeLibraryOntologyNode {
    [key: string]: Object | string | boolean | KnowledgeLibraryOntologyNode[] | undefined;
    nodeId: string;
    nodeText: string;
    icon: string;
    nodeChild?: KnowledgeLibraryOntologyNode[];
    expanded: boolean;
}

function convertToDropDownTreeData(node: IFacilityHierarchyPart): KnowledgeLibraryOntologyNode {
    return {
        nodeId: node.ID,
        nodeText: node.Name,
        icon: !node.OntologyParentID ? 'faciliy-icon' : 'building-icon',
        nodeChild: node.Children?.length ? node.Children.map(convertToDropDownTreeData) : undefined,
        expanded: true
    };
}

function getSelectedOntologyId(value: unknown): string | undefined {
    if (Array.isArray(value)) {
        return typeof value[0] === 'string' && value[0] ? value[0] : undefined;
    }

    return typeof value === 'string' && value ? value : undefined;
}

const KnowledgeLibraryOntologyFilter: React.FC<KnowledgeLibraryOntologyFilterProps> = ({
    facilityHierarchy,
    selectedOntologyId,
    onChange,
    readonly = false,
    showClearButton = true,
}) => {
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const dropDownRef = React.useRef<DropDownTreeComponent | null>(null);
    const suppressNextOpenRef = React.useRef(false);
    const resetSuppressTimeoutRef = React.useRef<number | null>(null);

    const fields = React.useMemo(() => {
        if (!facilityHierarchy) {
            return {
                dataSource: [],
                value: 'nodeId',
                text: 'nodeText',
                child: 'nodeChild',
                iconCss: 'icon'
            };
        }

        return {
            dataSource: [convertToDropDownTreeData(facilityHierarchy)],
            value: 'nodeId',
            text: 'nodeText',
            child: 'nodeChild',
            iconCss: 'icon'
        };
    }, [facilityHierarchy]);

    const handleChange = React.useCallback((args: DdtChangeEventArgs): void => {
        if (!args.isInteracted) {
            return;
        }

        onChange(getSelectedOntologyId(args.value));
    }, [onChange]);

    const handleBeforeOpen = React.useCallback((args: DdtBeforeOpenEventArgs): void => {
        if (!suppressNextOpenRef.current) {
            return;
        }

        args.cancel = true;
        suppressNextOpenRef.current = false;
    }, []);

    React.useEffect(() => {
        const clearButton = containerRef.current?.querySelector('.e-clear-icon');
        if (!(clearButton instanceof HTMLElement)) {
            return;
        }

        const suppressOpen = (event: Event) => {
            suppressNextOpenRef.current = true;

            if (resetSuppressTimeoutRef.current) {
                window.clearTimeout(resetSuppressTimeoutRef.current);
            }

            resetSuppressTimeoutRef.current = window.setTimeout(() => {
                suppressNextOpenRef.current = false;
                resetSuppressTimeoutRef.current = null;
            }, 0);

            event.stopPropagation();
            dropDownRef.current?.hidePopup();
        };

        clearButton.addEventListener('mouseup', suppressOpen, true);
        clearButton.addEventListener('touchend', suppressOpen, true);

        return () => {
            clearButton.removeEventListener('mouseup', suppressOpen, true);
            clearButton.removeEventListener('touchend', suppressOpen, true);

            if (resetSuppressTimeoutRef.current) {
                window.clearTimeout(resetSuppressTimeoutRef.current);
                resetSuppressTimeoutRef.current = null;
            }
        };
    }, [selectedOntologyId]);

    if (!facilityHierarchy) {
        return null;
    }

    return (
        <div className="kb-ontology-filter" ref={containerRef}>
            <div className="kb-ontology-filter-control">
                <DropDownTreeComponent
                    ref={dropDownRef}
                    fields={fields}
                    change={handleChange}
                    beforeOpen={handleBeforeOpen}
                    changeOnBlur={false}
                    placeholder={InovxLoader.t('facility:knowledgeBook.ontology')}
                    popupHeight="200px"
                    enabled={!readonly}
                    cssClass="facility-dropdown kb-ontology-filter-dropdown"
                    value={selectedOntologyId ? [selectedOntologyId] : []}
                    showClearButton={showClearButton}
                />
            </div>
        </div>
    );
};

export default KnowledgeLibraryOntologyFilter;
